<template lang="pug">
#family-message-list
  // title
  .d-flex.flex-row.justify-content-center.mb-3
    .page-title 送信履歴（家族）

  // filter
  .mb-2.d-flex.flex-row.align-items-center.flex-wrap(v-if="showFilter == 0 && !isBusy")
    span.table-option-text 記載者：
    .filter
      b-form-select(
        id="filter-input"
        v-model="filter"
        :options="filterOptions"
        size="md"
      )

  // message list
  MessageHistoryTable(
    v-model="messageItems"
    :fields="messageFields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isBusy="isBusy"
    :filter="filter"
    :filterFields="filterFields"
    @onDetail="onDetail"
  )
  // pagenation
  .d-flex.flex-row.justify-content-center(v-if="!isBusy")
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isBusy"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, mobileMessageHistoryStore, itemListFilterStore } from "@/store/store";
import { MobileMessageHistoryState } from "@/store/MobileMessageHistory";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import { errorHandle } from "@/errorHandler";
import Setting from "../../../../public/setting.json";

import MessagesService from "@/commons/services/Messages";
import MessageModel, { FunctionType } from "@/commons/models/Message";
import SendMessageReadCountsService, { SendMessageReadCountsModel } from "@/commons/services/SendMessageReadCounts";
import SendMessageRepliesService, { SendMessageRepliesUnreadModel } from "@/commons/services/SendMessageReplies";
import StandardAccountsService, { StandardAccountsGetModel } from "@/commons/services/StandardAccounts";
import { ShowMessage } from "@/commons/services/Authorization";

import MessageHistoryTable, { MessageHistoryTableField, MessageHistoryTableItem } from "@/mobile/components/tables/MessageHistoryTable.vue";
import SystemError from "@/commons/models/SystemError";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### メッセージ送信履歴(家族)コンポーネント
 */
@Component({
  components: {
    MessageHistoryTable
  }
})
export default class FamilyMessageList extends Vue {
  /** メッセージサービス */
  @Inject("MessagesService") messagesService!: MessagesService;
  /** メッセージ(既読/未読)サービス */
  @Inject("SendMessageReadCountsService") sendMessageReadCountsService!: SendMessageReadCountsService;
  /** メッセージ送信の返信サービス */
  @Inject("SendMessageRepliesService") sendMessageRepliesService!: SendMessageRepliesService;
  /** アカウントサービス */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;

  /** 読込み中か否か */
  isBusy: boolean = true;

  /** メッセージ履歴列名 */
  messageFields: Array<MessageHistoryTableField> = [];
  /** メッセージ履歴の情報 */
  messageItems: Array<MessageHistoryTableItem> = [];
  /** フィルタリングを表示するかどうか */
  showFilter: number = userStore.loginUser.permissions.showMessages;
  /** フィルタリング条件 */
  filter: any = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** フィルタリング対象カラム名 */
  filterFields: string[] = ["writerId"];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** メッセージ履歴1ページあたりの表示数 */
  perPage: number = Number(Setting.sendMessage.perPage);
  /** ページングの全体行数 */
  rows: number = 0;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.sendMessage.pageRange;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const messageFields: Array<MessageHistoryTableField> = this.createMessageFields();
    this.messageFields = messageFields;

    // 記載者フィルタリングが必要な場合はフィルタリング用のリストを作る
    if (userStore.loginUser.permissions.showMessages == ShowMessage.ALL) {
      const accounts: Array<StandardAccountsGetModel> = await this.getAccounts();
      const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(accounts);
      this.filterOptions = filterOptions;
      // フィルタの復元
      this.restoreFilterSetting();
    }

    // 動的に変更する画面情報を作成する
    this.messageItems = [];
    this.currentRows = this.messageItems.length;
    await this.createView();

    // ページ番号の復元は画面初回表示時でのみ行う
    // pageRowが設定されるまえにcurrentPageを復元してもライブラリがページ遷移をしてくれないので情報取得後にページ番号を復元する
    this.currentPage = this.$route.params.page ? Number(this.$route.params.page) : 1;

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createMessageFields(): Array<MessageHistoryTableField> {
    let fields: Array<MessageHistoryTableField> = [];
    fields.push({ key: "message", label: "", sortable: false, sortDirection: "desc", class: "" });

    return fields;
  }

  /**
   * ### アカウントを取得する
   */
  public async getAccounts(): Promise<Array<StandardAccountsGetModel>> {
    const params: any = { all: "true" };
    const response: Array<StandardAccountsGetModel> = await this.standardAccountsService.get(userStore, params);

    return response;
  }

  /**
   * ### 記載者グループフィルター用選択項目
   */
  public createFilterOptions(accounts: Array<StandardAccountsGetModel>): Array<{ value: string; text: string }> {
    let filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    for (const account of accounts) {
      filterOptions.push({
        value: `${account.userId}`,
        text: account.name
      });
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name + "-mobile"] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name + "-mobile"];
    }
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // テーブルに渡す各プロパティを作成する
    const messages: Array<MessageModel> = await this.getMessage();
    const messageItems: Array<MessageHistoryTableItem> = this.createMessageItems(messages);
    this.messageItems = this.messageItems.concat(messageItems);
    this.currentRows = this.messageItems.length;

    this.isBusy = false;

    // 画面表示後、既読・未読件数を取得する
    // DBのスループット上限問題、chromeのリクエスト上限を回避するために50件ずつ取得し、取得する毎に1秒インターバルを挟む
    let promises: Array<Promise<void>> = [];
    for (let i = 0; i < this.messageItems.length; i++) {
      if (this.messageItems[i].unreadCount == undefined) {
        let promise: Promise<void> | null = this.setUnread(i);
        if (promise != null) {
          promises.push(promise);
        }
      }
      if (this.messageItems[i].replyUnread == undefined) {
        let promise: Promise<void> | null = this.setReplyUnread(i);
        if (promise != null) {
          promises.push(promise);
        }
      }
      if (Setting.requestLimit < promises.length) {
        const results: Array<any> = await Promise.all(promises);
        // 非同期処理中に例外が発生していればresultの中にSystemerrorが格納されているのでチェックする
        for (const result of results) {
          if (result instanceof SystemError) {
            errorHandle(result, this);
          }
        }
        promises = [];
        await this.$timerUtil.setTimeout(1000);
      }
    }
    let results: Array<any> = await Promise.all(promises);
    for (const result of results) {
      if (result instanceof SystemError) {
        errorHandle(result, this);
      }
    }

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows(): Promise<void> {
    try {
      const params: any = {
        functionType: FunctionType.SEND_MESSAGE
      };
      const count: number = await this.messagesService.getCount(userStore, params);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### メッセージ履歴を取得するAPI呼び出し
   */
  public async getMessage(): Promise<Array<MessageModel>> {
    // ページ番号の復元がありえるので、ページ番号を復元する
    // 復元後かつページ遷移した場合、復元したcurrentPageよりもthis.currentPageが大きくなるのでその場合はthis.currentPageを使用する
    let currentPage: number = this.$route.params.page ? Number(this.$route.params.page) : 1;
    currentPage = this.currentPage < currentPage ? currentPage : this.currentPage;

    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象よりも多めに取得しておく
    const startIndex: number = this.currentRows;
    const endIndex: number = this.perPage * (currentPage + this.pageRange);
    const params: any = {
      functionType: FunctionType.SEND_MESSAGE,
      startIndex: startIndex,
      endIndex: endIndex,
      order: "desc"
    };
    const messages: Array<MessageModel> = await this.messagesService.gets(userStore, params);

    return messages;
  }

  /**
   * ### メッセージ履歴の行を作成する
   */
  public createMessageItems(messages: Array<MessageModel>): Array<MessageHistoryTableItem> {
    let items: Array<MessageHistoryTableItem> = [];

    for (const message of messages) {
      // 宛先文字列を作成する
      let receivers: string = "";
      if (message.receiverDetails) {
        for (let receiver of message.receiverDetails) {
          receivers = `${receivers}、${receiver.name}`;
        }
        // 先頭に「、」が入っているので、消す
        receivers = receivers.slice(1);
      }

      items.push({
        facilityId: message.facilityId,
        senderType: message.senderType,
        datetime: Number(message.datetime),
        receivers: receivers,
        functionContents: message.functionContents,
        writerId: message.writerId,
        writtenBy: message.writtenBy,
        unreadCount: undefined,
        replyUnread: undefined,
        receiverDetails: message.receiverDetails ? message.receiverDetails : []
      });
    }

    return items;
  }

  /**
   * ### 未読家族を取得する
   */
  public setUnread(no: number): Promise<void> | null {
    // 読みましたボタンが無い場合は取得処理不要
    if (this.messageItems[no].functionContents.addReadButton === false) {
      return null;
    }

    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        const message: any = this.messageItems[no];
        const readModel: SendMessageReadCountsModel = await this.sendMessageReadCountsService.get(userStore, Number(message.datetime));

        // 非同期処理中に画面遷移していた場合、このコンポーネントvueインスタンスが無くなっている可能性がある
        // 存在しない場合は何も処理しない
        if (this.messageItems != undefined && this.messageItems[no] != undefined) {
          this.messageItems[no]["unreadCount"] = readModel.unreadCounts;
          this.$forceUpdate();
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### 返信の未読を取得する
   */
  public setReplyUnread(no: number): Promise<void> | null {
    // 返信ボタンが無い場合は取得処理不要
    if (this.messageItems[no].functionContents.addReplyButton === false) {
      return null;
    }

    return new Promise(async (resolve: Function, reject: Function) => {
      try {
        let message: any = this.messageItems[no];
        const showSendMessageIncoming: SendMessageRepliesUnreadModel = await this.sendMessageRepliesService.getUnread(userStore, Number(message.datetime));

        // 非同期処理中に画面遷移していた場合、このコンポーネントvueインスタンスが無くなっている可能性がある
        // 存在しない場合は何も処理しない
        if (this.messageItems != undefined && this.messageItems[no] != undefined) {
          this.messageItems[no]["replyUnread"] = showSendMessageIncoming.unread;
          this.$forceUpdate();
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: MessageHistoryTableItem): void {
    mobileMessageHistoryStore.updateMobileMessageHistoryState({ state: item as MobileMessageHistoryState });
    this.$router.push({ name: MobileUrl.FamilyMessageHistoryDetail, params: { type: "1", page: String(this.currentPage) } });

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name + "-mobile", filter: this.filter });
    }
    next();
  }
}
</script>
